import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import type { CompanySupportRequest } from '@paladin-monorepo/common';

import { AuthLayout } from '@/layouts/AuthLayout';
import { AuthFormShell } from '@/components/PageHeader';
import { AuthSubmitButton } from '@/components/auth/AuthSubmitButton';
import {
  authFieldClassName,
  authLabelClassName,
  authLinkClassName,
} from '@/components/auth/authStyles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import API from '@/lib/api';

interface CompanyExistsLocationState {
  companyId?: string;
  companyName?: string;
}

interface SupportFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  message: string;
}

const schema = yup.object().shape({
  firstName: yup.string().required('Jméno je povinné'),
  lastName: yup.string().required('Příjmení je povinné'),
  email: yup.string().email('Neplatný e-mail').required('E-mail je povinný'),
  phone: yup.string().required('Telefon je povinný'),
  message: yup.string().required('Zpráva je povinná'),
});

export default function CompanyExistsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = location.state as CompanyExistsLocationState | null;

  const companyId = locationState?.companyId ?? '';
  const companyName = locationState?.companyName ?? '';

  const [isSubmitting, setIsSubmitting] = useState(false);

  const methods = useForm<SupportFormValues>({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      message: '',
    },
  });

  const onSubmit = async (values: SupportFormValues) => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await API.post('/auth/company-support-request', {
        companyId,
        companyName,
        ...values,
      } satisfies CompanySupportRequest);

      navigate('/auth/signup/request-sent');
    } catch {
      toast.error(t('signup.companyExists.errors.submitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!companyId) {
    navigate('/auth/signup');
    return null;
  }

  return (
    <AuthLayout contentWidth="wide">
      <AuthFormShell
        title={t('signup.companyExists.title')}
        description={t('signup.companyExists.description', { companyName, companyId })}
      >
        <div className="mb-6 flex justify-center">
          <div className="flex size-12 items-center justify-center rounded-full bg-brand-primary/15">
            <AlertTriangle className="size-6 text-brand-primary" />
          </div>
        </div>

        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div className="flex flex-col gap-5">
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={methods.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={authLabelClassName}>
                        {t('signup.companyExists.form.firstName')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          className={authFieldClassName}
                          placeholder={t('signup.companyExists.form.firstNamePlaceholder')}
                          disabled={isSubmitting}
                          autoComplete="given-name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={methods.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className={authLabelClassName}>
                        {t('signup.companyExists.form.lastName')}
                      </FormLabel>
                      <FormControl>
                        <Input
                          className={authFieldClassName}
                          placeholder={t('signup.companyExists.form.lastNamePlaceholder')}
                          disabled={isSubmitting}
                          autoComplete="family-name"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={methods.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={authLabelClassName}>
                      {t('signup.companyExists.form.email')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        className={authFieldClassName}
                        type="email"
                        placeholder={t('signup.companyExists.form.emailPlaceholder')}
                        disabled={isSubmitting}
                        autoComplete="email"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={methods.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={authLabelClassName}>
                      {t('signup.companyExists.form.phone')}
                    </FormLabel>
                    <FormControl>
                      <Input
                        className={authFieldClassName}
                        type="tel"
                        placeholder={t('signup.companyExists.form.phonePlaceholder')}
                        disabled={isSubmitting}
                        autoComplete="tel"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={methods.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className={authLabelClassName}>
                      {t('signup.companyExists.form.message')}
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        className="rounded-md border-border-subtle bg-white text-brand-secondary placeholder:text-brand-secondary/50 dark:bg-card dark:text-foreground dark:placeholder:text-muted-foreground"
                        placeholder={t('signup.companyExists.form.messagePlaceholder')}
                        disabled={isSubmitting}
                        rows={4}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <AuthSubmitButton disabled={isSubmitting} type="submit">
                {isSubmitting
                  ? t('signup.companyExists.form.submitting')
                  : t('signup.companyExists.form.submit')}
              </AuthSubmitButton>

              <Button
                variant="ghost"
                type="button"
                className={authLinkClassName}
                onClick={() => navigate('/auth/login')}
              >
                {t('signup.login.backToLogin')}
              </Button>
            </div>
          </form>
        </FormProvider>
      </AuthFormShell>
    </AuthLayout>
  );
}
